import { NgIf } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { FieldTypeConfig, FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { forEach, isUndefined } from 'angular';
import { Observable, Subject, asyncScheduler, filter, firstValueFrom, forkJoin, of, race, switchMap, take, takeUntil } from 'rxjs';
import { MonthsArray } from '@app/arrays/months.array';
import { PendoProfilesArray } from '@app/arrays/pendo-profiles.array';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_WRAPPERS, HubStatus } from '@app/constants';
import { DialogOptions, DialogService, FeedbackService, LoadingService, SocketService } from '@app/core/services';
import { AchSetupArray, ErrorMessages } from '@app/enums';
import { FiscalYearMonthStatusModel, UnitSetupControlInfo } from '@app/models/system-settings';
import { VendorService } from '@app/modules/fund-management/accounts-payable/vendors/services/vendor.service';
import { ButtonBarComponent, TitleComponent } from '@app/shared/components';
import { FormComponent } from '@app/shared/forms/components';
import { CustomerForEdit } from '@app/shared/models';
import { CustomerService, UnitService } from '@app/shared/services';
import { SiteService } from '@app/shared/services/site.service';
import { UtilsService } from '@app/shared/services/utils.service';
import { LinqSocketsService } from '@app/shared/sockets/services';
import { HubData } from '@app/shared/sockets/shared/interfaces';
import { isNil, mapArrayToSelectOptions } from '@app/utils';
import { IsGlobalAdmin, ReadOnly } from '@app/utils/claims';
import { fiscalYearTabFields } from './fields/fiscal-year-tab.fields';
import { generalInfoTabFields } from './fields/general-info-tab.fields';
import { salesTaxTabFields } from './fields/sales-tax-tab.fields';
import { UnitSetupConfig } from './fields/unit-setup-config.interface';
import { unitTabFields } from './fields/unit-tab.fields';

@Component({
	selector: 'unit-setup',
	standalone: true,
	imports: [TitleComponent, FormComponent, ButtonBarComponent, NgIf],
	templateUrl: './unit-setup.component.html',
	styleUrl: './unit-setup.component.less',
	encapsulation: ViewEncapsulation.None,
})
export class UnitSetupComponent implements OnInit, OnDestroy {
	constructor(
		private readonly unitService: UnitService,
		private readonly utilsService: UtilsService,
		private readonly vendorService: VendorService,
		private readonly siteService: SiteService,
		private readonly customerService: CustomerService,
		private readonly loadingService: LoadingService,
		private readonly feedbackService: FeedbackService,
		private readonly dialogService: DialogService,
		private readonly socketService: SocketService,
		private readonly linqSocketsService: LinqSocketsService,
	) {}

	@ReadOnly() isReadOnly = true;
	@IsGlobalAdmin() isGA = false;

	form = new UntypedFormGroup({});
	model = {} as UnitSetupModel;
	formlyFields: FormlyFieldConfig[] = [];
	formlyFormOptions: FormlyFormOptions = {
		formState: {
			mode: 'edit',
			selects: {
				sites: [],
				customers: [],
				pendoProfiles: PendoProfilesArray,
				states: [],
				achSetupArray: [],
				monthsArray: [],
				timeZones: [],
				createdFiscalYears: [],
			},
		},
	};
	selectedIndex = 0;
	destroy$ = new Subject();

	@HostListener('window:beforeunload')
	canDeactivate = (): boolean => !this.form.dirty;

	ngOnInit(): void {
		this.formlyFields = this.getFields();
		this.loadDependencies();
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	async cancelChanges(): Promise<void> {
		const unit = await firstValueFrom(this.unitService.getUnit());
		this.model = new UnitSetupModel(unit.unit, this.formlyFields);
		this.formlyFields = this.getFields();
		this.form.markAsPristine();
	}

	saveChanges(): void {
		this.feedbackService.clearAll();

		if (this.form.invalid) {
			const invalids: any = [];
			this.getInvalidTabIndexes(this.form, invalids);
			this.selectedIndex = invalids.length > 0 ? invalids[0] : 0;
			this.formlyFields = this.getFields();
			this.feedbackService.error('Error: One or more fields are invalid or missing.  Please make corrections and try save again.');
			return;
		}

		const editedUnit = this.model.getSavingModel();
		const fiscalYears = this.model.getEditedYears();

		this.saveFiscalYears(fiscalYears)
			.pipe(
				switchMap((hubData: HubData) => {
					if (hubData?.Status === HubStatus.Success) {
						return this.unitService.saveUnit(editedUnit).pipe(switchMap(() => of(true)));
					}
					return of(false);
				}),
			)
			.subscribe((saveSuccessful = false) => {
				if (saveSuccessful) {
					this.feedbackService.customSuccess('Unit Setup saved successfully!');
					this.cancelChanges();
				}
			});
	}

	createNewFiscalYear(): void {
		const selects = this.formlyFormOptions.formState.selects;
		const nextFiscalYear = selects.createdFiscalYears.length > 0 ? selects.createdFiscalYears[0].value + 1 : new Date().getFullYear();
		this.dialogService
			.open({
				disableClose: true,
				title: `Create New Fiscal Year ${nextFiscalYear}?`,
				message: `You have selected to create Fiscal Year: ${nextFiscalYear}.<br>Are you sure?`,
				okText: 'YES',
			} as DialogOptions)
			.pipe(
				switchMap((confirmed: boolean) => {
					if (confirmed) {
						this.loadingService.show();
						return this.unitService.queueCreateFiscalYear(nextFiscalYear).pipe(
							switchMap((processId: string) =>
								race(
									this.socketService.error$.pipe(
										takeUntil(this.destroy$),
										filter((notification: HubData) => notification.Id === processId),
										take(1),
									),
									this.socketService.success$.pipe(
										takeUntil(this.destroy$),
										filter((notification: HubData) => notification.Id === processId),
										take(1),
									),
								),
							),
						);
					}
					return of(null);
				}),
			)
			.subscribe((hubData?: HubData) => {
				if (this.loadingService.loading$.getValue() === true) {
					this.loadingService.hide();
				}

				if (!isNil(hubData)) {
					const { Status, Id, Data } = hubData;
					if (Status === HubStatus.Success) {
						this.model.tabFiscalYear.FiscalYears.push(Data.Entity);
						selects.createdFiscalYears = mapArrayToSelectOptions(this.model.tabFiscalYear.FiscalYears, {
							value: 'FiscalYear',
							label: 'FiscalYear',
							order: 'desc',
							all: false,
							sort: 'label',
						});
						this.feedbackService.customSuccess(`Fiscal Year ${nextFiscalYear} created successfully!`);
						const ctrl = this.form.controls['tabFiscalYear'].get('SelectedFiscalYearForClosedMonths');
						ctrl.patchValue(nextFiscalYear, { emitEvent: true });
					} else {
						const message = Data?.LinqValidationResult?.Errors[0] ?? ErrorMessages.ErrorContactAdministrator;
						this.feedbackService.error(message);
					}
					asyncScheduler.schedule(() => this.linqSocketsService.removeProcess(Id), 500);
				}
			});
	}

	/**
	 *  createEventHubCustomer creates a new Event Hub Customer: Fee Management.
	 *  It will prompt the user to confirm the creation of a new Event Hub Customer.
	 */
	createEventHubCustomer(): void {
		const feeMgmtCustomer = {
			CustomerName: 'Fee Management',
			Active: true,
		} as CustomerForEdit;

		// Validate Fee Management Customer does not already exist.
		const feeMgmtExists = this.formlyFormOptions.formState.selects.customers.some(
			(customer: any) => customer.label.toLowerCase() === feeMgmtCustomer.CustomerName.toLowerCase(),
		);

		if (feeMgmtExists) {
			this.feedbackService.clearAll();
			this.feedbackService.error(
				`Customer: ${feeMgmtCustomer.CustomerName} already exists!  ` +
					`Please select "${feeMgmtCustomer.CustomerName}" for Hub Customer under Hub Integration.`,
			);
			return;
		}

		this.dialogService
			.open({
				disableClose: true,
				title: `Create Event Hub Customer?`,
				message:
					`You have selected to create an Event Hub Customer.  ` +
					`Customer: ${feeMgmtCustomer.CustomerName} will be created and used ` +
					`for processing fee payments that are received via the Hub.<br>Are you sure?`,
				okText: 'YES',
			} as DialogOptions)
			.pipe(switchMap((confirmed: boolean) => (confirmed ? this.customerService.createEntity(feeMgmtCustomer) : of(null))))
			.subscribe((results: any) => {
				if (isNil(results)) {
					return;
				}

				const { isValid, customerId } = results;
				if (isValid) {
					this.feedbackService.customSuccess(`Event Hub Customer: ${feeMgmtCustomer.CustomerName} created successfully!`);
					feeMgmtCustomer.Id = customerId;
					this.setEventHubCustomerAfterCreation(feeMgmtCustomer);
				} else {
					this.feedbackService.error(`Creation of Customer: ${feeMgmtCustomer.CustomerName} failed!`);
				}
			});
	}

	/**
	 *  setEventHubCustomerAfterCreation is called after the Event Hub Customer is created.
	 *  It will update the Customer list stored in the form state
	 *  and set the selected customer for the Event Hub.
	 */
	setEventHubCustomerAfterCreation(entity: CustomerForEdit): void {
		const selects = this.formlyFormOptions.formState.selects;
		const newCustomer = { label: entity.CustomerName, value: entity.Id };
		selects.customers = [...this.formlyFormOptions.formState.selects.customers, newCustomer];

		// Refresh the form control with the new customer.
		this.form.get('tabUnitConfiguration')?.get('EventHubCustomerId')?.patchValue(entity.Id, { emitEvent: true });
		this.form.get('tabUnitConfiguration')?.get('EventHubCustomerId')?.markAsDirty();
	}

	getFiscalYearMonthElements(): FormlyFieldConfig[] {
		const fieldGroup = [];

		// Build the months based off the start month in order.
		// Month01 will be the start month, then the rest of the months will be in order.
		// Month13 will not be displayed until further notice.  2025-01-14

		const monthsBasedOffStartMonth = [];
		const startMonth = this.model?.tabFiscalYear?.StartMonth;
		if (!isNil(startMonth)) {
			monthsBasedOffStartMonth.push({ id: 1, description: startMonth });
			const startMonthIndex = MonthsArray.findIndex((m) => m.month === startMonth);
			for (const month of MonthsArray.slice(startMonthIndex + 1)) {
				monthsBasedOffStartMonth.push({ id: monthsBasedOffStartMonth.length + 1, description: month.month });
			}
			if (monthsBasedOffStartMonth.length < 12) {
				for (const month of MonthsArray) {
					if (monthsBasedOffStartMonth.length < 12) {
						monthsBasedOffStartMonth.push({ id: monthsBasedOffStartMonth.length + 1, description: month.month });
					}
				}
			}
			// Month 13 will be added but will not be visible until further notice.
			monthsBasedOffStartMonth.push({ id: 13, description: 'Month 13' });

			forEach(monthsBasedOffStartMonth, (monthItem) => {
				const monthCheckbox = {
					key: `Month${monthItem.id.toString().padStart(2, '0')}`,
					type: FORMLY_FIELD_TYPES.checkbox,
					className: 'cb-default',
					props: {
						label: monthItem.description,
						appearance: 'fill',
						indeterminate: false,
						disabled: this.isReadOnly,
						change: (field: FieldTypeConfig) => {
							this.model?.updateClosedMonth(
								field?.model?.SelectedFiscalYearForClosedMonths,
								field.key.toString(),
								field?.formControl?.value || false,
							);
						},
					},
					expressions: {
						// Hiding Month 13 until further notice.
						hide: () => monthItem.id === 13,
					},
				};

				fieldGroup.push(monthCheckbox);
			});
		} else {
			fieldGroup.push({
				type: FORMLY_FIELD_TYPES.headerLabel,
				className: 'linq-pl-5',
				props: {
					label: 'Closed Months cannot be edited until Start and End Month is selected and saved.',
					useElementH: '5',
					bold: true,
					danger: true,
				},
			});
		}

		return fieldGroup;
	}

	refreshTabFiscalYear(field: FormlyFieldConfig): void {
		field.form.patchValue({ ...this.model.tabFiscalYear }, { emitEvent: false });
	}

	async initSalesTaxVendor(field: FormlyFieldConfig): Promise<void> {
		if (!isNil(field?.formControl?.value)) {
			this.loadingService.skipNextRequest();
			const response = await firstValueFrom(this.vendorService.getVendor(field.formControl.value));
			const valueAsObject = response.vendor;
			field.form.get(field.key.toString())?.patchValue(valueAsObject, { emitEvent: true });
			field.form.get(field.key.toString())?.markAsPristine();
		}
	}

	private getFields(): FormlyFieldConfig[] {
		const config = {
			isGA: this.isGA,
			isReadOnly: this.isReadOnly,
			createNewFiscalYear: () => this.createNewFiscalYear(),
			createEventHubCustomer: () => this.createEventHubCustomer(),
			getFiscalYearMonthElements: () => this.getFiscalYearMonthElements(),
			refreshClosedMonths: (fiscalYear: number) => this.model.refreshClosedMonths(fiscalYear),
			refreshTabFiscalYear: (field: FormlyFieldConfig) => this.refreshTabFiscalYear(field),
			vendorSearchFilter: (value: string) => {
				this.loadingService.skipNextRequest();
				return this.vendorService.search(value, 100, false, [], true, null);
			},
			initSalesTaxVendor: (field: FormlyFieldConfig) => this.initSalesTaxVendor(field),
		} as UnitSetupConfig;

		return [
			{
				// The first FieldGroup with class 'row' is needed to pull the sectionSingleField wrapper
				// in line with the TitleComponent at the top of the page.

				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						fieldGroupClassName: 'unit-setup-page-container',
						wrappers: [FORMLY_FIELD_WRAPPERS.sectionSingleField],
						props: {
							section: 'mainContainer',
							excludePanelHeader: true,
							class: '',
						},
						fieldGroup: [
							{
								fieldGroupClassName: 'linq-tab-bar-no-bottom-margin',
								fieldGroup: [
									{
										type: FORMLY_FIELD_TYPES.formlyTabs,
										props: {
											stretchTabs: true,
											selectedIndex: this.selectedIndex,
											change: (_field: FieldTypeConfig, index: number) => {
												this.selectedIndex = index;
											},
										},
										fieldGroup: [
											{
												key: 'tabUnitConfiguration',
												props: { label: 'Unit Configuration', isTab: true },
												fieldGroup: unitTabFields(config),
											},
											{
												key: 'tabGeneralInfo',
												props: { label: 'General Information', isTab: true },
												fieldGroup: generalInfoTabFields(config),
											},
											{
												key: 'tabFiscalYear',
												props: { label: 'Fiscal Year', isTab: true },
												fieldGroup: fiscalYearTabFields(config),
											},
											{
												key: 'tabSalesTax',
												props: { label: 'Sales Tax', isTab: true },
												fieldGroup: salesTaxTabFields(config),
											},
										],
									},
								],
							},
						],
					},
				],
			},
		];
	}

	private saveFiscalYears(fiscalYears: FiscalYearMonthStatusModel[]): Observable<HubData> {
		const defaultHubData = { Data: { Entity: [] }, Status: HubStatus.Success, Message: null } as HubData;

		if (isNil(fiscalYears) || fiscalYears.length === 0) {
			return of(defaultHubData);
		}

		this.loadingService.show();
		return this.unitService.queueUpdateFiscalYears(fiscalYears).pipe(
			switchMap((processId: string) =>
				race(
					this.socketService.error$.pipe(
						takeUntil(this.destroy$),
						filter((notification: HubData) => notification.Id === processId),
						take(1),
					),
					this.socketService.success$.pipe(
						takeUntil(this.destroy$),
						filter((notification: HubData) => notification.Id === processId),
						take(1),
					),
				),
			),
			switchMap((hubData?: HubData) => {
				if (this.loadingService.loading$.getValue() === true) {
					this.loadingService.hide();
				}
				const { Status, Id } = hubData || defaultHubData;
				if (Status === HubStatus.Success) {
					asyncScheduler.schedule(() => this.linqSocketsService.removeProcess(Id), 500);
					return of(hubData);
				} else {
					this.feedbackService.error('Error: Failed to save fiscal years.');
					defaultHubData.Status = HubStatus.Error;
					return of(defaultHubData);
				}
			}),
		);
	}

	private getInvalidTabIndexes(formGroup: UntypedFormGroup, invalids: any = [], index = -1): void {
		Object.keys(formGroup.controls).forEach((key) => {
			const control = formGroup.get(key);
			if (control instanceof UntypedFormGroup) {
				index++;
				if (control.invalid) {
					invalids.push(index);
				}
				this.getInvalidTabIndexes(control, invalids, index);
			}
		});
	}

	private loadDependencies(): void {
		const unit$ = this.unitService.getUnit();
		const sites$ = this.siteService.getList();
		const customers$ = this.customerService.getList();
		const states$ = this.utilsService.getStates();
		const timeZones$ = this.utilsService.getTimeZones();

		forkJoin({ unitData: unit$, siteData: sites$, customerData: customers$, stateData: states$, timeZonesData: timeZones$ }).subscribe(
			(data: any) => {
				const unit = data.unitData.unit;
				const fiscalYears = unit.FiscalYears ?? [];
				const countryData = data.stateData.states;
				const timeZoneData = data.timeZonesData;
				const selects = this.formlyFormOptions.formState.selects;

				selects.sites = mapArrayToSelectOptions(
					data.siteData.sites.map((m: any) => ({ Id: m.Id, Description: `${m.SiteCode} - ${m.SiteName}` })),
					{ value: 'Id', label: 'Description', all: false, sort: 'label' },
				);

				selects.customers = mapArrayToSelectOptions(
					data.customerData?.customers.map((m: any) => ({ Id: m.Id, Description: m.CustomerName })),
					{ value: 'Id', label: 'Description', all: false, sort: 'label' },
				);

				// Set US States
				const usCountry = countryData.find((f: any) => f.CountryCode === 'US');
				selects.states = mapArrayToSelectOptions(
					usCountry.StateCodes.map((m: any) => ({ Id: m.StateCode, Description: m.StateDisplay })),
					{ value: 'Id', label: 'Description', all: false, sort: 'label' },
				);

				selects.achSetupArray = mapArrayToSelectOptions(AchSetupArray, { value: 'Id', label: 'Description', all: false, sort: 'label' });

				selects.monthsArray = mapArrayToSelectOptions(MonthsArray, { value: 'month', label: 'month', all: false, sort: 'label' });

				selects.timeZones = mapArrayToSelectOptions(timeZoneData, { value: 'Id', label: 'Description', all: false, sort: 'label' });

				selects.createdFiscalYears = mapArrayToSelectOptions(fiscalYears, {
					value: 'FiscalYear',
					label: 'FiscalYear',
					order: 'desc',
					all: false,
					sort: 'label',
				});

				this.model = new UnitSetupModel(unit, this.formlyFields);
				this.formlyFields = this.getFields();
			},
		);
	}
}

/**
 * Internal Model used by Unit Setup Component
 */
class UnitSetupModel {
	private readonly unit: any;

	tabUnitConfiguration: any;
	tabGeneralInfo: any;
	tabFiscalYear: any;
	tabSalesTax: any;

	constructor(unit: any, fields: FormlyFieldConfig[]) {
		this.unit = unit;
		const keyArray: any = [];
		this.evaluateFields(fields, false, keyArray);
		this.loadModel(keyArray);
	}

	private loadModel(keyArray: any[]): void {
		keyArray.forEach((fieldsArray) => {
			forEach(fieldsArray, (internalArray) => {
				let tabName: any = null;
				forEach(internalArray, (item) => {
					if (item.isTab) {
						tabName = item.key;
						(this as any)[tabName] = {};
					} else if (!item.isTab && !isNil(tabName) && !isNil(this.unit[item.key])) {
						(this as any)[tabName][item.key] = this.unit[item.key];
					}
				});
			});
		});

		// Clone FiscalYears for Closed Months.
		if (!isNil(this.tabFiscalYear)) {
			this.tabFiscalYear.FiscalYears = [...(this.unit?.FiscalYears || [])];
		}
	}
	private evaluateFields(fields: FormlyFieldConfig[], isNested = false, keyArray: any = []): void {
		fields.forEach((field) => {
			const arrayCount = keyArray.length;
			if (!isNested) {
				keyArray.push({ [arrayCount]: [] });
			}

			if (field.key || field.type) {
				keyArray[keyArray.length - 1][keyArray.length - 1].push({ key: field.key, type: field.type, isTab: field.props?.isTab || false });
			}

			if (field.fieldGroup) {
				this.evaluateFields(field.fieldGroup, true, keyArray);
			}
		});
	}
	public updateClosedMonth(fiscalYear: string, key: string, value: boolean): void {
		const fiscalYearIndex = this.tabFiscalYear.FiscalYears.findIndex((f: any) => f.FiscalYear === fiscalYear);
		this.tabFiscalYear.FiscalYears[fiscalYearIndex][key] = value;
		this.tabFiscalYear.FiscalYears[fiscalYearIndex].dirty = true;
	}
	public refreshClosedMonths(fiscalYear: number): Promise<boolean> {
		return new Promise((resolve) => {
			const fyObject = this.tabFiscalYear.FiscalYears.find((f: any) => f.FiscalYear === fiscalYear);
			for (let i = 1; i <= 13; i++) {
				const key = `Month${i.toString().padStart(2, '0')}`;
				this.tabFiscalYear[key] = fyObject[key];
			}
			resolve(true);
		});
	}
	public getSavingModel(): UnitSetupControlInfo {
		const editedUnit = {} as UnitSetupControlInfo;
		const tabs = ['tabUnitConfiguration', 'tabGeneralInfo', 'tabFiscalYear', 'tabSalesTax'];
		Object.keys(this).forEach((parentKey) => {
			if (tabs.includes(parentKey)) {
				Object.keys((this as any)[parentKey]).forEach((key) => {
					if (!isUndefined(this.unit[key])) {
						(editedUnit as any)[key] = (this as any)[parentKey][key];
					}
				});
			}
		});

		return editedUnit;
	}
	public getEditedYears(): FiscalYearMonthStatusModel[] {
		return this.tabFiscalYear.FiscalYears.filter((f: any) => f.dirty);
	}
}
